<template>
  <div class="page-container">
    <!-- 功能关闭提示 -->
    <el-alert
      v-if="messageConfig.enable === false"
      title="站内信功能已关闭，如需开启请修改服务端 .env 中的 MESSAGE_ENABLE=true"
      type="warning"
      :closable="false"
      show-icon
      style="margin-bottom:12px"
    />

    <el-card class="search-card" shadow="never">
      <el-form :inline="true" :model="searchForm" size="small">
        <el-form-item label="消息标题">
          <el-input v-model="searchForm.title" placeholder="标题关键词" clearable style="width:180px" />
        </el-form-item>
        <el-form-item label="消息类型">
          <el-select v-model="searchForm.type" placeholder="全部" clearable style="width:120px">
            <el-option v-for="(label, val) in typeLabels" :key="val" :label="label" :value="val" />
          </el-select>
        </el-form-item>
        <el-form-item label="发送目标">
          <el-select v-model="searchForm.target_type" placeholder="全部" clearable style="width:120px">
            <el-option label="全部用户" :value="1" />
            <el-option label="指定用户" :value="2" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" size="mini" icon="el-icon-search" @click="handleSearch">查询</el-button>
          <el-button size="mini" icon="el-icon-refresh" @click="handleReset">重置</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <el-card shadow="never" style="margin-top:12px">
      <div slot="header" class="card-header">
        <span>站内信列表</span>
        <el-button
          v-permission="['member:message:create']"
          :disabled="messageConfig.enable === false"
          type="success" size="mini" icon="el-icon-s-promotion"
          @click="openSendDialog"
        >新建</el-button>
      </div>

      <el-table :data="tableData" v-loading="loading" border stripe size="small">
        <el-table-column type="index" label="#" width="55" align="center" />
        <el-table-column prop="title" label="标题" min-width="150" show-overflow-tooltip />
        <el-table-column label="类型" width="90" align="center">
          <template slot-scope="{ row }">
            <el-tag :type="typeTagMap[row.type]" size="mini">{{ typeLabels[row.type] }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="发送目标" width="100" align="center">
          <template slot-scope="{ row }">
            <el-tag v-if="row.target_type === 1" type="info" size="mini">全部用户</el-tag>
            <el-tag v-else type="warning" size="mini">指定用户 #{{ row.target_user_id }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="sender_name" label="发送人" width="100" />
        <el-table-column prop="content" label="内容预览" min-width="180" show-overflow-tooltip />
        <el-table-column prop="created_at" label="发送时间" width="165" />
        <el-table-column label="操作" width="100" align="center">
          <template slot-scope="{ row }">
            <el-button type="text" size="mini" @click="showDetail(row)">查看</el-button>
            <el-button
              v-permission="['member:message:delete']"
              type="danger" size="mini"
              @click="handleDelete(row)"
            >删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination-wrap">
        <el-pagination
          background
          :current-page="page"
          :page-size="pageSize"
          :page-sizes="[20, 50, 100]"
          :total="total"
          layout="total, sizes, prev, pager, next"
          @size-change="sz => { pageSize = sz; page = 1; fetchData() }"
          @current-change="p => { page = p; fetchData() }"
        />
      </div>
    </el-card>

    <!-- 发送消息弹窗 -->
    <el-dialog title="发送站内信" :visible.sync="sendDialogVisible" width="560px" @close="resetSendForm">
      <el-form ref="sendForm" :model="sendForm" :rules="sendRules" label-width="90px" size="small">
        <el-form-item label="消息类型" prop="type">
          <el-radio-group v-model="sendForm.type">
            <el-radio v-for="(label, val) in typeLabels" :key="val" :label="Number(val)">{{ label }}</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="发送目标" prop="target_type">
          <el-radio-group v-model="sendForm.target_type">
            <el-radio :label="1">全部用户</el-radio>
            <el-radio :label="2">指定用户</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item v-if="sendForm.target_type === 2" label="用户ID" prop="target_user_id">
          <el-input-number v-model="sendForm.target_user_id" :min="1" style="width:160px" placeholder="请输入用户ID" />
        </el-form-item>
        <el-form-item label="消息标题" prop="title">
          <el-input v-model="sendForm.title" placeholder="请输入消息标题" />
        </el-form-item>
        <el-form-item label="消息内容" prop="content">
          <el-input
            v-model="sendForm.content"
            type="textarea"
            :rows="4"
            placeholder="请输入消息内容"
          />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button size="mini" @click="sendDialogVisible = false">取消</el-button>
        <el-button type="primary" size="mini" :loading="sending" @click="handleSend">发送</el-button>
      </span>
    </el-dialog>

    <!-- 消息详情弹窗 -->
    <el-dialog title="消息详情" :visible.sync="detailVisible" width="520px">
      <div v-if="detailData" class="detail-wrap">
        <el-descriptions :column="2" border size="small">
          <el-descriptions-item label="消息标题" :span="2">{{ detailData.title }}</el-descriptions-item>
          <el-descriptions-item label="消息类型">
            <el-tag :type="typeTagMap[detailData.type]" size="mini">{{ typeLabels[detailData.type] }}</el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="发送目标">
            {{ detailData.target_type === 1 ? '全部用户' : `指定用户 #${detailData.target_user_id}` }}
          </el-descriptions-item>
          <el-descriptions-item label="发送人">{{ detailData.sender_name }}</el-descriptions-item>
          <el-descriptions-item label="发送时间">{{ detailData.created_at }}</el-descriptions-item>
        </el-descriptions>
        <div class="detail-content">
          <div class="detail-content-label">消息内容</div>
          <div class="detail-content-body">{{ detailData.content }}</div>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getMessageConfig, getMessageList, sendMessage, deleteMessage } from '@/api/message'

export default {
  name: 'Message',
  data() {
    return {
      loading: false,
      tableData: [],
      total: 0,
      page: 1,
      pageSize: 20,
      messageConfig: { enable: true },
      typeLabels: { 1: '系统通知', 2: '活动通知', 3: '个人消息' },
      typeTagMap: { 1: 'primary', 2: 'warning', 3: 'success' },
      searchForm: { title: '', type: '', target_type: '' },
      // 发送弹窗
      sendDialogVisible: false,
      sending: false,
      sendForm: { type: 1, target_type: 1, target_user_id: null, title: '', content: '' },
      sendRules: {
        title:   [{ required: true, message: '请输入标题', trigger: 'blur' }],
        content: [{ required: true, message: '请输入内容', trigger: 'blur' }]
      },
      // 详情弹窗
      detailVisible: false,
      detailData: null
    }
  },
  created() {
    this.fetchConfig()
    this.fetchData()
  },
  methods: {
    async fetchConfig() {
      try {
        const res = await getMessageConfig()
        this.messageConfig = res.data || { enable: true }
      } catch {}
    },
    async fetchData() {
      this.loading = true
      try {
        const res = await getMessageList({ ...this.searchForm, page: this.page, per_page: this.pageSize })
        this.tableData = res.data.list || []
        this.total = res.data.total || 0
      } finally {
        this.loading = false
      }
    },
    handleSearch() { this.page = 1; this.fetchData() },
    handleReset() {
      this.searchForm = { title: '', type: '', target_type: '' }
      this.page = 1
      this.fetchData()
    },
    openSendDialog() {
      this.sendForm = { type: 1, target_type: 1, target_user_id: null, title: '', content: '' }
      this.sendDialogVisible = true
    },
    resetSendForm() {
      this.$refs.sendForm && this.$refs.sendForm.resetFields()
    },
    async handleSend() {
      try {
        await this.$refs.sendForm.validate()
        this.sending = true
        const data = { ...this.sendForm }
        if (data.target_type !== 2) delete data.target_user_id
        await sendMessage(data)
        this.$message.success('发送成功')
        this.sendDialogVisible = false
        this.page = 1
        this.fetchData()
      } catch {} finally {
        this.sending = false
      }
    },
    async handleDelete(row) {
      try {
        await this.$confirm(`确定删除消息「${row.title}」吗？`, '提示', { type: 'warning' })
        await deleteMessage({ id: row.id })
        this.$message.success('删除成功')
        this.fetchData()
      } catch {}
    },
    showDetail(row) {
      this.detailData = row
      this.detailVisible = true
    }
  }
}
</script>

<style scoped>
.page-container { padding: 16px; }
.card-header { display: flex; justify-content: space-between; align-items: center; }
.pagination-wrap { margin-top: 16px; text-align: right; }
.detail-wrap { }
.detail-content { margin-top: 16px; }
.detail-content-label { font-size: 13px; color: #606266; font-weight: 600; margin-bottom: 8px; }
.detail-content-body {
  background: #f5f7fa;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  padding: 12px;
  font-size: 14px;
  line-height: 1.8;
  white-space: pre-wrap;
  word-break: break-all;
  min-height: 80px;
}
</style>
